export type UnitConversion = {
	fromUnit: string;
	toUnit: string;
	factor: string;
	consumableId?: number;
};

export type ConsumableSettings = {
	categories: string[];
	brands: string[];
	units: string[];
	conversions: UnitConversion[];
};

export const DEFAULT_CONSUMABLE_CATEGORIES = [
	"Cables",
	"Fiber optic",
	"Connectors",
	"Fasteners",
	"Pipes",
	"General",
] as const;

export const DEFAULT_CONSUMABLE_UNITS = ["pcs", "meter", "roll", "box", "pack", "kg"] as const;

export function mergeUniqueLists(defaults: readonly string[], custom: string[]): string[] {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const value of [...defaults, ...custom]) {
		const trimmed = value.trim();
		if (!trimmed) continue;
		const key = trimmed.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(trimmed);
	}
	return out;
}

export function nextConsumableCode(rows: Array<{ code?: string }>): string {
	let max = 0;
	for (const row of rows) {
		const number = Number((row.code ?? "").replace(/^CON-/i, ""));
		if (Number.isFinite(number)) max = Math.max(max, number);
	}
	return `CON-${max + 1}`;
}

export function parseQty(value: string): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
}

export function formatQty(value: number): string {
	if (!Number.isFinite(value)) return "0";
	const rounded = Math.round(value * 1000) / 1000;
	return String(rounded);
}

export function convertQtyToUnit(
	qty: number,
	fromUnit: string,
	toUnit: string,
	conversions: UnitConversion[],
	consumableId?: number,
): number {
	const from = fromUnit.trim().toLowerCase();
	const to = toUnit.trim().toLowerCase();
	if (!from || !to || from === to) return qty;
	const relevant = conversions.filter((row) => !row.consumableId || row.consumableId === consumableId);
	for (const row of relevant) {
		const rowFrom = row.fromUnit.trim().toLowerCase();
		const rowTo = row.toUnit.trim().toLowerCase();
		const factor = Number(row.factor);
		if (!Number.isFinite(factor) || factor <= 0) continue;
		if (rowFrom === from && rowTo === to) return qty * factor;
		if (rowFrom === to && rowTo === from) return qty / factor;
	}
	return qty;
}

export function qtyAfterMovement(
	currentQty: string,
	movementQty: string,
	movementUnit: string,
	primaryUnit: string,
	conversions: UnitConversion[],
	consumableId: number | undefined,
	type: "in" | "out" | "transfer",
): string {
	if (type === "transfer") return currentQty;
	const base = parseQty(currentQty);
	const delta = convertQtyToUnit(parseQty(movementQty), movementUnit, primaryUnit, conversions, consumableId);
	const next = type === "in" ? base + delta : base - delta;
	return formatQty(Math.max(0, next));
}

export function isLowStock(qty: string, minQty: string): boolean {
	const min = parseQty(minQty);
	if (min <= 0) return false;
	return parseQty(qty) <= min;
}

export function groupConsumablesByCategory<T extends { category?: string }>(rows: T[]): Array<[string, T[]]> {
	const map = new Map<string, T[]>();
	for (const row of rows) {
		const category = row.category?.trim() || "Uncategorised";
		const bucket = map.get(category);
		if (bucket) bucket.push(row);
		else map.set(category, [row]);
	}
	return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, "en", { sensitivity: "base" }));
}
